/**
 * UploadZone — shared XER uploader. Renders a dropzone + hidden file input.
 * On success, calls `onUploaded(data)` where data is the /api/upload response.
 * While uploading it shows the §24 AdaptiveCountdown; on failure it renders the
 * §25 catalog message (by error code, never the raw server message).
 *
 * Single-file (default): choosing a file uploads immediately. A multi-project
 * file returns `{ status:'multi_project', projects }` → one ProjectPicker → the
 * SAME file is re-submitted with `selected_proj_id` → 201 → onUploaded.
 *
 * Compare to baseline (Sub-2b): a toggle reveals a second `baseline_file` slot.
 * The request carries `file` (+ `baseline_file`); when EITHER side is
 * multi-project the backend returns `{ status:'multi_project', needs:[{ which,
 * projects }] }` (Sub-2a contract). We resolve every entry with a per-file
 * ProjectPicker (current then baseline), then re-submit BOTH files + the
 * relevant ids (`selected_proj_id` / `baseline_selected_proj_id`) in ONE POST.
 *
 * A 2xx the uploader cannot act on must NEVER silently reset to the form
 * (§24/§25) — the Stage 5.1 forensic bug.
 */
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client.js';
import { uploadErrorMessage } from '../api/uploadErrors.js';
import { ModeSelector } from './ModeSelector.jsx';
import { ProjectPicker } from './ProjectPicker.jsx';
import { AdaptiveCountdown } from './AdaptiveCountdown.jsx';
import { estimateSeconds, foldQueueEstimate } from '../utils/estimateProcessing.js';
import { canWireGzip, gzipFileForWire } from '../utils/wireCompress.js';
import { remainingSeconds, updateRateBps } from '../utils/remainingSeconds.js';

// LAUNCH-CLOSE B: the active job ticket survives a reload — polling resumes
// instead of losing the upload. sessionStorage (per-tab, cleared on close).
const ACTIVE_JOB_KEY = 'cw_active_upload_job';

function readActiveJob() {
  try { return sessionStorage.getItem(ACTIVE_JOB_KEY); } catch { return null; }
}
function writeActiveJob(jobId) {
  try {
    if (jobId) sessionStorage.setItem(ACTIVE_JOB_KEY, jobId);
    else sessionStorage.removeItem(ACTIVE_JOB_KEY);
  } catch { /* storage unavailable — polling still works, resume doesn't */ }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function UploadZone({
  onUploaded,
  onBlocked,
  variant = 'prominent',
  disabled = false,
  disabledReason = null,
}) {
  const [state, setState] = useState('idle'); // idle | uploading | picker | error
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('general');
  const [estimateS, setEstimateS] = useState(0);
  // UPLOAD-WIRE B: the driven countdown — one honest number from wall-clock +
  // real transfer progress. Engages once progress or the 202 exists; until
  // then the legacy decelerating estimate renders (§24 continuity).
  const [displayRemaining, setDisplayRemaining] = useState(null);
  const wireRef = useRef({
    loaded: 0, total: 0, lastLoaded: 0, lastMs: 0,
    rateBps: null, acceptedAt: null, serverEstimate: null,
    gotProgress: false, allowReset: false, shown: null,
  });

  const resetWire = () => {
    wireRef.current = {
      loaded: 0, total: 0, lastLoaded: 0, lastMs: 0,
      rateBps: null, acceptedAt: null, serverEstimate: null,
      gotProgress: false, allowReset: false, shown: null,
    };
    setDisplayRemaining(null);
  };

  // 500 ms wall-clock tick: recompute from Date.now() (a starved tab catches
  // up instantly — never decrement-per-tick). Non-increasing except the single
  // permitted reset when the 202's estimate replaces the pre-upload one.
  useEffect(() => {
    if (state !== 'uploading') return undefined;
    const id = setInterval(() => {
      const w = wireRef.current;
      if (!w.gotProgress && !w.acceptedAt) return; // legacy mode until data exists
      const computed = remainingSeconds({
        now: Date.now(),
        acceptedAt: w.acceptedAt ?? undefined,
        loaded: w.loaded,
        total: w.total,
        rateBps: w.rateBps,
        serverEstimate: w.serverEstimate,
      });
      let next = computed;
      if (w.shown != null && !w.allowReset) next = Math.min(w.shown, computed);
      w.allowReset = false;
      w.shown = next;
      setDisplayRemaining(next);
    }, 500);
    return () => clearInterval(id);
  }, [state]);
  const [compareMode, setCompareMode] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [baselineFile, setBaselineFile] = useState(null);
  // picker = { needs:[{ which:'current'|'baseline', projects }], selections:{ which: projId } }
  const [picker, setPicker] = useState(null);
  const inputRef = useRef(null);
  const baselineInputRef = useRef(null);

  // LAUNCH-CLOSE B: poll cancellation on unmount — the loop checks the ref
  // each tick and simply stops (the job itself keeps running server-side;
  // the sessionStorage ticket lets a remount resume it).
  const pollStopRef = useRef(false);
  useEffect(() => () => { pollStopRef.current = true; }, []);

  // Poll the job ticket until done|failed. 2 s cadence, 5 s after the first
  // minute. A single failed poll is a blip — only 5 CONSECUTIVE transport
  // errors fail the flow; a 404 is a real answer (job unknown/lost, e.g. a
  // server restart) and fails immediately via the §25 graceful copy.
  const pollJob = async (jobId) => {
    let consecutiveErrors = 0;
    const startedMs = Date.now();
    for (;;) {
      if (pollStopRef.current) return;
      await sleep(Date.now() - startedMs > 60_000 ? 5_000 : 2_000);
      if (pollStopRef.current) return;
      let job;
      try {
        job = await api.jobStatus(jobId);
        consecutiveErrors = 0;
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          writeActiveJob(null);
          setState('error');
          setError(new ApiError(200, 'processing_failed', null));
          return;
        }
        consecutiveErrors += 1;
        if (consecutiveErrors >= 5) {
          writeActiveJob(null);
          setState('error');
          setError(e);
          return;
        }
        continue;
      }
      if (job && job.status === 'done') {
        writeActiveJob(null);
        setState('idle');
        onUploaded?.({ report_id: job.report_id, ...(job.result || {}) });
        return;
      }
      if (job && job.status === 'failed') {
        writeActiveJob(null);
        setState('error');
        setError(new ApiError(200, job.failure === 'over_capacity' ? 'over_capacity' : 'processing_failed', null));
        return;
      }
      // queued | processing → keep polling under the countdown.
    }
  };

  // Resume a job left behind by a reload: the ticket is in sessionStorage and
  // the server retains records 60 min after completion.
  useEffect(() => {
    const jobId = readActiveJob();
    if (!jobId || typeof api.jobStatus !== 'function') return;
    setState('uploading');
    setEstimateS(30);
    pollJob(jobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One submit path for the first upload AND every picker re-submit. The held
  // files come from state; `selections` carries any resolved project ids. Files
  // can be passed explicitly to avoid reading just-set state (auto-submit case).
  const submit = async ({ current, baseline, selections = {} } = {}) => {
    const curFile = current !== undefined ? current : currentFile;
    const blFile = baseline !== undefined ? baseline : baselineFile;
    if (!curFile) return;
    const ownEstimate = estimateSeconds((curFile.size || 0) + (blFile?.size || 0));
    setEstimateS(ownEstimate);
    resetWire();
    wireRef.current.serverEstimate = ownEstimate;
    setState('uploading');
    setError(null);
    try {
      // UPLOAD-WIRE A: gzip large files on the wire (streamed; Railway's edge
      // closes any request after 5 minutes — compressed XER crosses in time).
      // Small files and CompressionStream-less browsers send exactly as today.
      const fd = new FormData();
      let anyCompressed = false;
      if (canWireGzip(curFile)) {
        const { blob, name } = await gzipFileForWire(curFile);
        fd.append('file', blob, name);
        anyCompressed = true;
      } else {
        fd.append('file', curFile);
      }
      if (blFile) {
        if (canWireGzip(blFile)) {
          const { blob, name } = await gzipFileForWire(blFile);
          fd.append('baseline_file', blob, name);
          anyCompressed = true;
        } else {
          fd.append('baseline_file', blFile);
        }
      }
      if (anyCompressed) fd.append('content_encoding', 'gzip');
      fd.append('mode', mode);
      if (selections.current) fd.append('selected_proj_id', selections.current);
      if (selections.baseline) fd.append('baseline_selected_proj_id', selections.baseline);

      // UPLOAD-WIRE A/B: XHR for the upload (fetch has no upload progress);
      // each progress event feeds the EMA rate for the honest countdown.
      // Partial test doubles without the method fall back to api.upload.
      const onProgress = ({ loaded, total }) => {
        const w = wireRef.current;
        const nowMs = Date.now();
        if (w.lastMs > 0) {
          w.rateBps = updateRateBps(w.rateBps, loaded - w.lastLoaded, nowMs - w.lastMs);
        }
        w.lastLoaded = loaded;
        w.lastMs = nowMs;
        w.loaded = loaded;
        w.total = total;
        w.gotProgress = true;
      };
      const uploadPromise = typeof api.uploadWithProgress === 'function'
        ? api.uploadWithProgress(fd, { onProgress })
        : api.upload(fd);
      // v3.20 §2: fold the queue position into the §24 countdown estimate —
      // fired AFTER the upload POST is initiated (the upload stays first on the
      // wire) and folded in when it lands. Best-effort in every direction: a
      // failed/slow status fetch (or an api surface without the method —
      // partial test doubles) leaves the own-size estimate untouched.
      if (typeof api.uploadQueueStatus === 'function') {
        api.uploadQueueStatus()
          .then((s) => {
            const folded = foldQueueEstimate(ownEstimate, s);
            setEstimateS(folded);
            wireRef.current.serverEstimate = folded;
          })
          .catch(() => {});
      }
      const data = await uploadPromise;

      // LAUNCH-CLOSE B: the job-ticket contract — 202 {job_id} immediately,
      // then poll under the SAME §24 countdown. UPLOAD-WIRE B: the 202 flips
      // the countdown to the processing phase; replacing the pre-upload
      // estimate is the ONE permitted display reset.
      if (data && data.job_id) {
        setPicker(null);
        const w = wireRef.current;
        w.acceptedAt = Date.now();
        if (data.estimate_seconds != null) {
          setEstimateS(data.estimate_seconds);
          w.serverEstimate = data.estimate_seconds;
        }
        w.allowReset = true;
        writeActiveJob(data.job_id);
        await pollJob(data.job_id);
        return;
      }
      if (data && data.report_id) {
        setPicker(null);
        setState('idle');
        onUploaded?.(data);
        return;
      }
      // Normalise both multi-project shapes into one `needs` list so there is a
      // single picker code path. Single-file ⇒ a one-entry `current` need.
      let needs = null;
      if (data && data.status === 'multi_project') {
        if (Array.isArray(data.needs)) needs = data.needs;
        else if (Array.isArray(data.projects)) needs = [{ which: 'current', projects: data.projects }];
      }
      if (needs) {
        setPicker({ needs, selections: { ...selections } });
        setState('picker');
        return;
      }
      // §24/§25: an unexpected but successful response is NOT silently dropped.
      setState('error');
      setError(new ApiError(200, 'unexpected_response', null));
    } catch (e) {
      setState('error');
      setError(e);
      if (e instanceof ApiError && onBlocked) onBlocked(e);
    }
  };

  // Choosing the current file auto-submits in single-file mode (unchanged); in
  // compare mode it is just held until the user presses Analyse (a baseline may
  // still be coming).
  const handleCurrentFile = (file) => {
    setCurrentFile(file);
    if (!compareMode) submit({ current: file });
  };

  const handlePick = (which, projId) => {
    if (!picker) return;
    const selections = { ...picker.selections, [which]: projId };
    const remaining = picker.needs.filter((n) => selections[n.which] === undefined);
    if (remaining.length === 0) {
      submit({ selections }); // every side resolved → one re-submit, both files + ids
    } else {
      setPicker({ ...picker, selections });
    }
  };

  const cancelPicker = () => {
    // Back to the form; the chosen files are kept so the user can retry.
    setPicker(null);
    setState('idle');
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (disabled) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) handleCurrentFile(f);
  };

  const buttonDisabled = disabled || state === 'uploading';
  const reasonId = disabled && disabledReason ? 'upload-zone-disabled-reason' : undefined;

  // The next outstanding side to resolve (current first, then baseline).
  const outstanding = picker
    ? picker.needs.find((n) => picker.selections[n.which] === undefined)
    : null;

  // Picker is its own focused state — the form is hidden so the user resolves
  // the ambiguity first. A comparison names which file is being picked.
  if (state === 'picker' && picker && outstanding) {
    const title = baselineFile
      ? (outstanding.which === 'current'
        ? 'Current schedule — choose a project'
        : 'Baseline — choose a project')
      : null;
    return (
      <section className={`upload-zone upload-zone-${variant} is-picking`} aria-label="Choose a project">
        <ProjectPicker
          projects={outstanding.projects}
          title={title}
          onSelect={(projId) => handlePick(outstanding.which, projId)}
          onCancel={cancelPicker}
        />
      </section>
    );
  }

  return (
    <section
      className={`upload-zone upload-zone-${variant} ${state === 'uploading' ? 'is-uploading' : ''}`}
      aria-busy={state === 'uploading'}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <ModeSelector value={mode} onChange={setMode} />

      <label className="compare-toggle">
        <input
          type="checkbox"
          checked={compareMode}
          onChange={(e) => setCompareMode(e.target.checked)}
        />
        <span>Compare to baseline</span>
      </label>
      {compareMode && (
        <p className="compare-hint">Upload an earlier baseline .xer to see what changed.</p>
      )}

      {/* Current-schedule file input (hidden, shared by both modes). */}
      <input
        ref={inputRef}
        type="file"
        accept=".xer"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleCurrentFile(f);
        }}
        style={{ display: 'none' }}
        aria-label="Choose .xer file"
        disabled={disabled}
      />

      {!compareMode ? (
        <>
          <button
            type="button"
            className="btn-submit"
            onClick={() => inputRef.current?.click()}
            disabled={buttonDisabled}
            aria-describedby={reasonId}
          >
            {state === 'uploading' ? 'Analysing…' : 'Choose .xer file'}
          </button>
          {reasonId && <span id={reasonId} className="sr-only">{disabledReason}</span>}
          {variant === 'prominent' && !disabled && state !== 'uploading' && (
            <p className="upload-hint">or drop a file here</p>
          )}
        </>
      ) : (
        <div className="compare-slots">
          <div className="upload-slot">
            <span className="upload-slot-label">Current schedule</span>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => inputRef.current?.click()}
              disabled={buttonDisabled}
            >
              {currentFile ? 'Change current .xer' : 'Choose current .xer file'}
            </button>
            {currentFile && <span className="upload-slot-file">{currentFile.name}</span>}
          </div>

          <div className="upload-slot">
            <span className="upload-slot-label">Baseline (.xer)</span>
            <input
              ref={baselineInputRef}
              type="file"
              accept=".xer"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setBaselineFile(f);
              }}
              style={{ display: 'none' }}
              aria-label="Choose baseline .xer file"
              disabled={disabled}
            />
            <button
              type="button"
              className="btn-secondary"
              onClick={() => baselineInputRef.current?.click()}
              disabled={buttonDisabled}
            >
              {baselineFile ? 'Change baseline .xer' : 'Choose baseline .xer file'}
            </button>
            {baselineFile && (
              <span className="upload-slot-file">
                <span className="upload-slot-name">{baselineFile.name}</span>
                <button
                  type="button"
                  className="btn-text"
                  onClick={() => setBaselineFile(null)}
                  disabled={buttonDisabled}
                  aria-label="Remove baseline file"
                >
                  Remove
                </button>
              </span>
            )}
          </div>

          <button
            type="button"
            className="btn-submit"
            onClick={() => submit({ current: currentFile, baseline: baselineFile })}
            disabled={buttonDisabled || !currentFile}
            aria-describedby={reasonId}
          >
            {state === 'uploading' ? 'Analysing…' : 'Analyse'}
          </button>
          {reasonId && <span id={reasonId} className="sr-only">{disabledReason}</span>}
        </div>
      )}

      {state === 'uploading' && (
        <AdaptiveCountdown
          estimateSeconds={estimateS}
          remainingSeconds={displayRemaining}
        />
      )}
      {state === 'error' && error && (
        <p className="upload-error" role="alert">
          {uploadErrorMessage(error)}
        </p>
      )}
    </section>
  );
}
